import { apiClient } from "@/services/apiClient";
import type { ProfessionalTotpState } from "@/services/professionalTotpService";

export interface NotaryUser {
  /**
   * Tri-state, not a boolean: "declared, cannot issue CFE" is a DIFFERENT
   * situation from "never declared" and needs a different response from an
   * admin (spec 82f26c86).
   */
  fiscalStatus: "complete" | "cannot_issue_cfe" | "not_declared";
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
  status: string;
  createdAt: string;
  notaryActivationStatus: "pending" | "active" | null;
  /**
   * Active assignments this professional holds across BOTH request tables,
   * against `activeAssignmentLimit`. Computed by the server — the cap is one
   * number for the person, not per discipline, and a surface that counted one
   * table would report half of it.
   */
  activeAssignments: number;
  activeAssignmentLimit: number;
}

export interface NotaryProfileDetail {
  /**
   * The fiscal declaration, read-only (spec 82f26c86). The activation gate
   * reads exactly these fields, so this is where an admin finds out why a
   * professional cannot be activated.
   */
  fiscalStatus: "complete" | "cannot_issue_cfe" | "not_declared";
  rut: string | null;
  razonSocial: string | null;
  issuesCfe: boolean | null;
  cfeProvider: string | null;
  id: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  secondName: string | null;
  secondLastName: string | null;
  profileImage: string | null;
  status: string;
  createdAt: string;
  emailVerifiedAt: string | null;
  identityVerifiedAt: string | null;
  cedula: string | null;
  notaryMatricula: string | null;
  notaryActivationStatus: "pending" | "active" | null;
  invitedByUserId: string | null;
  invitedByEmail: string | null;
  inviteAcceptedAt: string | null;
}

export interface NotaryRequestHistoryItem {
  id: string;
  requestedByUserId: string;
  applicantName: string | null;
  applicantEmail: string | null;
  status: string;
  requestType: string;
  acceptedAt: string | null;
  updatedAt: string;
  documentCount: number;
}

export interface NotaryDetailResponse {
  profile: NotaryProfileDetail;
  requestHistory: {
    items: NotaryRequestHistoryItem[];
    total: number;
    page: number;
    limit: number;
  };
  /**
   * Second-factor state, a sibling of `profile` rather than a field on it:
   * profile is identity and fiscal data, this is security state with its own
   * lifecycle (spec b8731a82). Identical to AccountantDetailResponse's block
   * by design — one shared card renders both.
   */
  totp: ProfessionalTotpState;
}

export interface PendingNotaryInvite {
  id: string;
  email: string;
  invitedByUserId: string;
  invitedByEmail: string | null;
  expiresAt: string;
  createdAt: string;
  expired: boolean;
}

export function listNotaries(): Promise<NotaryUser[]> {
  return apiClient.get<NotaryUser[]>("/platform/notaries").then((res) => res.data);
}

export function getNotaryDetail(
  notaryId: string,
  params?: { requestsPage?: number; requestsLimit?: number }
): Promise<NotaryDetailResponse> {
  const searchParams = new URLSearchParams();
  if (params?.requestsPage != null) {
    searchParams.set("requestsPage", String(params.requestsPage));
  }
  if (params?.requestsLimit != null) {
    searchParams.set("requestsLimit", String(params.requestsLimit));
  }
  const qs = searchParams.toString();
  return apiClient
    .get<NotaryDetailResponse>(
      `/platform/notaries/${encodeURIComponent(notaryId)}${qs ? `?${qs}` : ""}`
    )
    .then((res) => res.data);
}

export function listPendingNotaryInvites(): Promise<PendingNotaryInvite[]> {
  return apiClient
    .get<PendingNotaryInvite[]>("/platform/notary-invites")
    .then((res) => res.data);
}

export function inviteNotary(email: string): Promise<void> {
  return apiClient
    .post("/auth/notary-invite", { email: email.trim().toLowerCase() })
    .then(() => undefined);
}

export function resendNotaryInvite(id: string): Promise<void> {
  return apiClient.post(`/platform/notary-invites/${id}/resend`).then(() => undefined);
}

export function cancelNotaryInvite(id: string): Promise<void> {
  return apiClient.delete(`/platform/notary-invites/${id}`).then(() => undefined);
}

export function deleteNotary(userId: string): Promise<void> {
  return apiClient
    .delete<{ ok: true }>(`/platform/notaries/${userId}`)
    .then(() => undefined);
}

export function activateNotary(userId: string): Promise<void> {
  return apiClient
    .post(`/platform/notaries/${encodeURIComponent(userId)}/activate`)
    .then(() => undefined);
}
